import {
  useEffect, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { createRoot } from 'react-dom/client'
import { Check, ChevronDown, Loader2, X } from 'lucide-react'
import { initialen, persoonKleur } from '../lib/opmaak'
import { useThema } from '../lib/thema'
import logoUrl from '../assets/logo.webp'

/* ===========================================================================
 *  De bouwstenen
 *
 *  Overgenomen uit Projects, en die uit het dashboard: een knop is 32 hoog,
 *  een venster heeft een kop en een voet, geel alleen voor de ene primaire
 *  actie. Zonder het dashboard te importeren: dit is een eigen repo.
 * =========================================================================== */

type KnopSoort = 'hoofd' | 'gewoon' | 'stil' | 'gevaar'

export function Knop({
  soort = 'gewoon', klein, bezig, icoon, children, className, ...rest
}: {
  soort?: KnopSoort
  klein?: boolean
  bezig?: boolean
  icoon?: ReactNode
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`knop k-${soort} ${klein ? 'klein' : ''} ${children ? '' : 'alleen-icoon'} ${className ?? ''}`}
      disabled={rest.disabled || bezig}
      {...rest}
    >
      {bezig ? <Loader2 size={15} className="draai" /> : icoon}
      {children}
    </button>
  )
}

/**
 * Het merklogo, zoals in het dashboard (components/Logo.tsx daar).
 *
 * Het bestand is getekend voor een donker vlak: de witte delen verdwijnen op
 * wit. In het lichte thema komt er daarom een donker bordje onder, in plaats
 * van het logo te verkleuren -- dat zou het merk aantasten.
 */
export function Logo({ breedte = 96 }: { breedte?: number }) {
  const licht = useThema().actief === 'licht'
  return (
    <span className={`logo-vlak ${licht ? 'op-licht' : ''}`} style={{ width: breedte }}>
      <img src={logoUrl} alt="Truckwash1 Group" width={breedte} height={Math.round((breedte / 250) * 70)} draggable={false} />
    </span>
  )
}

export function Badge({ kleur = 'info', children, title }: { kleur?: string; children: ReactNode; title?: string }) {
  return <span className={`badge t-${kleur}`} title={title}>{children}</span>
}

export function Punt({ kleur }: { kleur: string }) {
  return <span className={`punt t-${kleur}`} aria-hidden />
}

export function Avatar({ naam, id, grootte = 22, url }: { naam: string | null | undefined; id?: string | null; grootte?: number; url?: string | null }) {
  if (url) {
    return <img className="avatar" src={url} alt={naam ?? ''} width={grootte} height={grootte} loading="lazy" style={{ width: grootte, height: grootte }} />
  }
  return (
    <span
      className={`avatar t-${persoonKleur(id ?? naam)}`}
      style={{ width: grootte, height: grootte, fontSize: Math.max(9, Math.round(grootte * 0.42)) }}
      title={naam ?? 'Niemand'}
    >
      {naam ? initialen(naam) : '–'}
    </span>
  )
}

export function Laden({ tekst }: { tekst?: string }) {
  return (
    <div className="laden">
      <Loader2 size={18} className="draai" />
      {tekst && <span>{tekst}</span>}
    </div>
  )
}

export function Leeg({ icoon, titel, tekst, children }: { icoon?: ReactNode; titel: string; tekst?: ReactNode; children?: ReactNode }) {
  return (
    <div className="leeg">
      {icoon && <div className="leeg-icoon">{icoon}</div>}
      <h3>{titel}</h3>
      {tekst && <p>{tekst}</p>}
      {children && <div className="leeg-acties">{children}</div>}
    </div>
  )
}

export function Foutvlak({ fout, opnieuw }: { fout: string; opnieuw?: () => void }) {
  return (
    <div className="foutvlak" role="alert">
      <span>{fout}</span>
      {opnieuw && <Knop klein onClick={opnieuw}>Opnieuw</Knop>}
    </div>
  )
}

export function Veld({ label, hulp, children, breed, verplicht }: { label: string; hulp?: ReactNode; children: ReactNode; breed?: boolean; verplicht?: boolean }) {
  return (
    <label className={`veld ${breed ? 'breed' : ''}`}>
      <span className="veld-label">{label}{verplicht && <span title="Verplicht"> *</span>}</span>
      {children}
      {hulp && <span className="veld-hulp">{hulp}</span>}
    </label>
  )
}

/* ------------------------------------------------------------------ *
 *  Venster en lade
 * ------------------------------------------------------------------ */

function useEscape(f: () => void, aan = true) {
  useEffect(() => {
    if (!aan) return
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); f() } }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [f, aan])
}

export function Venster({
  titel, onSluit, children, voet, breed, bewustSluiten,
}: {
  titel: ReactNode
  onSluit: () => void
  children: ReactNode
  voet?: ReactNode
  breed?: number
  /** Niet sluiten bij een klik ernaast: daar staat ingetypt werk in. */
  bewustSluiten?: boolean
}) {
  useEscape(onSluit)
  return createPortal(
    <div className="scherm" onMouseDown={(e) => { if (e.target === e.currentTarget && !bewustSluiten) onSluit() }}>
      <div className="venster" role="dialog" aria-modal style={breed ? { width: `min(${breed}px, calc(100vw - 32px))` } : undefined}>
        <div className="venster-kop">
          <h2>{titel}</h2>
          <Knop soort="stil" klein icoon={<X size={16} />} onClick={onSluit} aria-label="Sluiten" />
        </div>
        <div className="venster-lijf">{children}</div>
        {voet && <div className="venster-voet">{voet}</div>}
      </div>
    </div>,
    document.body,
  )
}

export function Lade({ onSluit, children, breed = 760 }: { onSluit: () => void; children: ReactNode; breed?: number }) {
  useEscape(onSluit)
  return createPortal(
    <div className="scherm lade-scherm" onMouseDown={(e) => { if (e.target === e.currentTarget) onSluit() }}>
      <aside className="lade" style={{ width: `min(${breed}px, 100vw)` }} role="dialog" aria-modal>
        {children}
      </aside>
    </div>,
    document.body,
  )
}

/** Een ja/nee-vraag, als belofte. */
export function vraagBevestiging(tekst: string, ja = 'Doorgaan'): Promise<boolean> {
  return new Promise((klaar) => {
    const plek = document.createElement('div')
    document.body.appendChild(plek)
    const wortel = createRoot(plek)
    const sluit = (antwoord: boolean) => {
      wortel.unmount()
      plek.remove()
      klaar(antwoord)
    }
    wortel.render(
      <Venster titel="Weet je het zeker?" onSluit={() => sluit(false)}
        voet={<>
          <Knop soort="stil" onClick={() => sluit(false)}>Annuleren</Knop>
          <Knop soort="gevaar" onClick={() => sluit(true)} autoFocus>{ja}</Knop>
        </>}>
        <p>{tekst}</p>
      </Venster>,
    )
  })
}

/* ------------------------------------------------------------------ *
 *  Een uitklapmenu dat zelf zijn plek zoekt
 * ------------------------------------------------------------------ */

export function Uitklap({
  knop, children, open, onOpen, uitlijnen = 'links', breed,
}: {
  knop: ReactNode
  children: ReactNode
  open: boolean
  onOpen: (o: boolean) => void
  uitlijnen?: 'links' | 'rechts'
  breed?: number
}) {
  const anker = useRef<HTMLSpanElement>(null)
  const [plek, setPlek] = useState<{ x: number; y: number; boven: boolean } | null>(null)

  useLayoutEffect(() => {
    if (!open || !anker.current) return
    const r = anker.current.getBoundingClientRect()
    const b = breed ?? 240
    const x = uitlijnen === 'rechts' ? Math.max(8, r.right - b) : Math.min(r.left, window.innerWidth - b - 8)
    const boven = r.bottom + 320 > window.innerHeight && r.top > 320
    setPlek({ x, y: boven ? r.top - 4 : r.bottom + 4, boven })
  }, [open, uitlijnen, breed])

  useEscape(() => onOpen(false), open)

  return (
    <span ref={anker} className="uitklap-anker">
      <span onClick={() => onOpen(!open)}>{knop}</span>
      {open && plek && createPortal(
        <>
          <div className="uitklap-vang" onMouseDown={() => onOpen(false)} />
          <div
            className="uitklap"
            style={{
              left: plek.x,
              ...(plek.boven ? { bottom: window.innerHeight - plek.y } : { top: plek.y }),
              width: breed,
            }}
          >
            {children}
          </div>
        </>,
        document.body,
      )}
    </span>
  )
}

export function MenuRegel({ icoon, children, onClick, gevaar, actief, sneltoets }: {
  icoon?: ReactNode; children: ReactNode; onClick?: () => void; gevaar?: boolean; actief?: boolean; sneltoets?: string
}) {
  return (
    <button type="button" className={`menu-regel ${gevaar ? 'gevaar' : ''} ${actief ? 'actief' : ''}`} onClick={onClick}>
      {icoon}
      <span className="menu-tekst">{children}</span>
      {actief && <Check size={14} />}
      {sneltoets && <kbd>{sneltoets}</kbd>}
    </button>
  )
}

/** Een keuze uit een korte lijst, als knop met een menu eronder. */
export function Kies<T extends string>({
  waarde, opties, onKies, render, leeg, klein, titel,
}: {
  waarde: T | null
  opties: { key: T; label: string; kleur?: string; icoon?: ReactNode }[]
  onKies: (w: T) => void
  render?: (o: { key: T; label: string; kleur?: string } | undefined) => ReactNode
  leeg?: string
  klein?: boolean
  titel?: string
}) {
  const [open, setOpen] = useState(false)
  const nu = opties.find((o) => o.key === waarde)
  return (
    <Uitklap open={open} onOpen={setOpen} breed={200}
      knop={
        <button type="button" className={`kies ${klein ? 'klein' : ''}`} title={titel}>
          {render ? render(nu) : <>{nu?.kleur && <Punt kleur={nu.kleur} />}{nu?.icoon}<span>{nu?.label ?? leeg ?? 'Kies'}</span></>}
          <ChevronDown size={13} className="kies-pijl" />
        </button>
      }>
      {opties.map((o) => (
        <MenuRegel key={o.key} actief={o.key === waarde}
          icoon={o.icoon ?? (o.kleur ? <Punt kleur={o.kleur} /> : undefined)}
          onClick={() => { onKies(o.key); setOpen(false) }}>
          {o.label}
        </MenuRegel>
      ))}
    </Uitklap>
  )
}

/** Iemand kiezen, met zoeken. Leeg = niemand. */
export function Tabs<T extends string>({ waarde, tabs, onKies }: {
  waarde: T
  tabs: { key: T; label: string; icoon?: ReactNode; teller?: number }[]
  onKies: (t: T) => void
}) {
  return (
    <div className="tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} type="button" role="tab" aria-selected={t.key === waarde}
          className={`tab ${t.key === waarde ? 'actief' : ''}`} onClick={() => onKies(t.key)}>
          {t.icoon}
          <span>{t.label}</span>
          {t.teller !== undefined && t.teller > 0 && <span className="teller">{t.teller}</span>}
        </button>
      ))}
    </div>
  )
}

/** Een balkje voortgang: klaar van totaal. */
export function Voortgang({ klaar, totaal, kleur = 'ok' }: { klaar: number; totaal: number; kleur?: string }) {
  const p = totaal ? Math.round((klaar / totaal) * 100) : 0
  return (
    <div className={`voortgang t-${kleur}`} title={`${klaar} van ${totaal} (${p}%)`}>
      <div style={{ width: `${p}%` }} />
    </div>
  )
}
